import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  Key,
  Monitor,
  Play,
  Server,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { drmApi } from '@/api/client';
import type { DrmStats, DrmKey, DrmSession, DrmDevice } from '@/types';

type TabId = 'overview' | 'keys' | 'sessions' | 'devices';

const tabs: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'keys', label: 'Keys' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'devices', label: 'Devices' },
];

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-status-healthy/20 text-status-healthy',
    rotated: 'bg-jeket-info/20 text-jeket-info',
    revoked: 'bg-status-error/20 text-status-error',
    expired: 'bg-status-error/20 text-status-error',
    released: 'bg-jeket-silver-muted/20 text-jeket-silver-muted',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-medium capitalize', colors[status] ?? 'bg-jeket-silver-muted/20 text-jeket-silver-muted')}>
      {status}
    </span>
  );
}

function DrmSystemBadge({ system }: { system: string }) {
  const colors: Record<string, string> = {
    widevine: 'bg-blue-500/20 text-blue-400',
    fairplay: 'bg-purple-500/20 text-purple-400',
    playready: 'bg-green-500/20 text-green-400',
    clearkey: 'bg-jeket-gold/20 text-jeket-gold',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-medium capitalize', colors[system] ?? 'bg-jeket-silver-muted/20 text-jeket-silver-muted')}>
      {system}
    </span>
  );
}

function formatTimestamp(ts: number): string {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleString();
}

function truncateId(id: string, len = 12): string {
  if (id.length <= len) return id;
  return id.slice(0, len) + '...';
}

// ---- Overview Tab ----

function OverviewTab({ stats }: { stats?: DrmStats }) {
  if (!stats) return null;

  const cards = [
    { label: 'Total Keys', value: stats.total_keys, icon: Key, color: 'text-jeket-gold' },
    { label: 'Active Sessions', value: stats.active_sessions, icon: Play, color: 'text-status-healthy' },
    { label: 'Total Devices', value: stats.total_devices, icon: Monitor, color: 'text-jeket-info' },
    { label: 'Active Backends', value: stats.active_backends, icon: Server, color: 'text-purple-400' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <div key={card.label} className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-jeket-silver-muted">{card.label}</span>
              <card.icon size={18} className={card.color} />
            </div>
            <p className="text-2xl font-bold text-jeket-silver-bright">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl p-5">
        <h3 className="text-sm font-semibold text-jeket-silver-bright mb-3">Registered Backends</h3>
        <div className="flex flex-wrap gap-2">
          {stats.backends.map((name: string) => (
            <DrmSystemBadge key={name} system={name} />
          ))}
          {stats.backends.length === 0 && (
            <span className="text-sm text-jeket-silver-muted">No backends registered</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Keys Tab ----

function KeysTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['drm-keys'],
    queryFn: () => drmApi.listKeys(),
    refetchInterval: 10_000,
  });

  const keys: DrmKey[] = data?.keys ?? [];

  if (isLoading) {
    return <TableSkeleton />;
  }

  return (
    <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-jeket-black-elevated text-left text-jeket-silver-muted">
            <th className="px-4 py-3 font-medium">Key ID</th>
            <th className="px-4 py-3 font-medium">Content ID</th>
            <th className="px-4 py-3 font-medium">Track Type</th>
            <th className="px-4 py-3 font-medium">Scheme</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <tr key={key.id} className="border-b border-jeket-black-elevated/50 hover:bg-jeket-black-elevated/30">
              <td className="px-4 py-3 font-mono text-jeket-silver-bright">{truncateId(key.key_id, 16)}</td>
              <td className="px-4 py-3 text-jeket-silver-muted">{truncateId(key.content_id)}</td>
              <td className="px-4 py-3 text-jeket-silver-muted">{key.track_type}</td>
              <td className="px-4 py-3 text-jeket-silver-muted font-mono">{key.protection_scheme}</td>
              <td className="px-4 py-3"><StatusBadge status={key.status} /></td>
              <td className="px-4 py-3 text-jeket-silver-muted">{formatTimestamp(key.created_at)}</td>
            </tr>
          ))}
          {keys.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-jeket-silver-muted">
                No content keys found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---- Sessions Tab ----

function SessionsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['drm-sessions'],
    queryFn: () => drmApi.listSessions(),
    refetchInterval: 10_000,
  });

  const sessions: DrmSession[] = data?.sessions ?? [];

  if (isLoading) {
    return <TableSkeleton />;
  }

  return (
    <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-jeket-black-elevated text-left text-jeket-silver-muted">
            <th className="px-4 py-3 font-medium">Session ID</th>
            <th className="px-4 py-3 font-medium">User</th>
            <th className="px-4 py-3 font-medium">Content</th>
            <th className="px-4 py-3 font-medium">DRM System</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Renewals</th>
            <th className="px-4 py-3 font-medium">Started</th>
            <th className="px-4 py-3 font-medium">Last Heartbeat</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.id} className="border-b border-jeket-black-elevated/50 hover:bg-jeket-black-elevated/30">
              <td className="px-4 py-3 font-mono text-jeket-silver-bright">{truncateId(s.id)}</td>
              <td className="px-4 py-3 text-jeket-silver-muted">{truncateId(s.user_id)}</td>
              <td className="px-4 py-3 text-jeket-silver-muted">{truncateId(s.content_id)}</td>
              <td className="px-4 py-3"><DrmSystemBadge system={s.drm_system} /></td>
              <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
              <td className="px-4 py-3 text-jeket-silver-muted">{s.renewal_count}</td>
              <td className="px-4 py-3 text-jeket-silver-muted">{formatTimestamp(s.started_at)}</td>
              <td className="px-4 py-3 text-jeket-silver-muted">{formatTimestamp(s.last_heartbeat)}</td>
            </tr>
          ))}
          {sessions.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-8 text-center text-jeket-silver-muted">
                No playback sessions found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---- Devices Tab ----

function DevicesTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['drm-devices'],
    queryFn: () => drmApi.listDevices(),
    refetchInterval: 10_000,
  });

  const devices: DrmDevice[] = data?.devices ?? [];

  if (isLoading) {
    return <TableSkeleton />;
  }

  return (
    <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-jeket-black-elevated text-left text-jeket-silver-muted">
            <th className="px-4 py-3 font-medium">Device ID</th>
            <th className="px-4 py-3 font-medium">User</th>
            <th className="px-4 py-3 font-medium">DRM System</th>
            <th className="px-4 py-3 font-medium">Platform</th>
            <th className="px-4 py-3 font-medium">Model</th>
            <th className="px-4 py-3 font-medium">HDCP</th>
            <th className="px-4 py-3 font-medium">Revoked</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => (
            <tr key={d.id} className="border-b border-jeket-black-elevated/50 hover:bg-jeket-black-elevated/30">
              <td className="px-4 py-3 font-mono text-jeket-silver-bright">{truncateId(d.device_id)}</td>
              <td className="px-4 py-3 text-jeket-silver-muted">{truncateId(d.user_id)}</td>
              <td className="px-4 py-3"><DrmSystemBadge system={d.drm_system} /></td>
              <td className="px-4 py-3 text-jeket-silver-muted">{d.platform || '-'}</td>
              <td className="px-4 py-3 text-jeket-silver-muted">{d.model || '-'}</td>
              <td className="px-4 py-3">
                <span className={cn(
                  'px-2 py-0.5 rounded text-xs font-medium',
                  d.hdcp_supported
                    ? 'bg-status-healthy/20 text-status-healthy'
                    : 'bg-jeket-silver-muted/20 text-jeket-silver-muted'
                )}>
                  {d.hdcp_supported ? `v${d.hdcp_version}` : 'No'}
                </span>
              </td>
              <td className="px-4 py-3">
                {d.revoked ? (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-status-error/20 text-status-error">
                    Revoked
                  </span>
                ) : (
                  <span className="text-jeket-silver-muted text-xs">-</span>
                )}
              </td>
            </tr>
          ))}
          {devices.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-jeket-silver-muted">
                No devices found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---- Table Skeleton ----

function TableSkeleton() {
  return (
    <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl p-8">
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-jeket-gold" />
      </div>
    </div>
  );
}

// ---- Main Page ----

export default function DRM() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const { data: stats } = useQuery({
    queryKey: ['drm-stats'],
    queryFn: drmApi.getStats,
    refetchInterval: 10_000,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield size={24} className="text-jeket-gold" />
        <div>
          <h1 className="text-xl font-bold text-jeket-silver-bright">DRM Management</h1>
          <p className="text-sm text-jeket-silver-muted">Content keys, playback sessions, and device management</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-jeket-black-elevated">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === tab.id
                ? 'text-jeket-gold border-jeket-gold'
                : 'text-jeket-silver-muted border-transparent hover:text-jeket-silver-bright'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab stats={stats} />}
      {activeTab === 'keys' && <KeysTab />}
      {activeTab === 'sessions' && <SessionsTab />}
      {activeTab === 'devices' && <DevicesTab />}
    </div>
  );
}
